// @flow

import React from 'react';
import * as R from 'ramda';
import { Dialog, Button, useModal } from '@8base/boost';
import { useMutation } from 'react-apollo';
import errorCodes from '@8base/error-codes';

import { i18n } from 'i18n';
import { ForcibleErrorDialog } from 'dialogs/ForcibleErrorDialog';
import { TOAST_SUCCESS_MESSAGE } from 'common/constants/apolloOperationContextOptions';

import { USER_DELETE_MUTATION } from 'graphql/mutations';

import { Trans } from 'utils/translate';

const USER_DELETE_DIALOG_ID = 'USER_DELETE_DIALOG_ID';

const UserDeleteDialog = () => {
  const { closeModal, openModal } = useModal();

  const onClose = () => closeModal(USER_DELETE_DIALOG_ID);

  const [userDelete, { loading: submitting }] = useMutation(USER_DELETE_MUTATION, {
    refetchQueries: ['UsersList', 'UsersStatistics'],
    context: {
      [TOAST_SUCCESS_MESSAGE]: i18n.t('dialogs.userDelete.successMessage'),
    },
  });

  const createOnDelete = ({ id }) => async () => {
    window.trackEvent('Users', 'Delete user');

    const identifiers = id instanceof Array ? id : [id];

    await Promise.all(identifiers.map((id) => userDelete({ variables: { data: { id }}}).catch((err) => ({ id, err })))).then((errors) => {
      const forcibleErrors = R.filter(R.pathEq(['err', 'graphQLErrors', 0, 'code'], errorCodes.ForcibleErrorCode), errors);

      if (forcibleErrors.length > 0) {
        openModal(ForcibleErrorDialog.ID, {
          onSubmit: () => Promise.all(forcibleErrors.map(({ id }) => userDelete({ variables: { data: { id }, force: true }}))),
        });
      }
    });

    onClose();
  };

  return (
    <Dialog id={ USER_DELETE_DIALOG_ID } size="sm">
      { ({ args }) => (
        <React.Fragment>
          <Dialog.Header title={ i18n.t('dialogs.userDelete.title', { count: args.id.length || 1 }) } onClose={ onClose } />
          <Dialog.Body>
            <Trans i18nKey="dialogs.userDelete.message" count={ args.id.length || 1 }>
              Are you sure you want to delete this user?
            </Trans>
          </Dialog.Body>
          <Dialog.Footer>
            <Button color="neutral" variant="outlined" onClick={ onClose }>
              <Trans i18nKey="shared.cancel">Cancel</Trans>
            </Button>
            <Button
              type="submit"
              loading={ submitting }
              onClick={ createOnDelete(args) }
              color="danger"
            >
              <Trans i18nKey="shared.delete">Delete</Trans>
            </Button>
          </Dialog.Footer>
        </React.Fragment>
      ) }
    </Dialog>
  );
};

UserDeleteDialog.ID = USER_DELETE_DIALOG_ID;

export { UserDeleteDialog };
